import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, isSameWeek, addDays, isToday, startOfDay, differenceInMinutes, parseISO } from 'date-fns';
import { attendanceApi, employeeApi, shiftApi } from '../services/api';
import { AttendanceRecord, Employee } from '../types';
import { useLocationContext } from '../components/Layout';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, ChevronDown, ChevronRight as ChevronRightIcon, Clock, Plus, RefreshCw, Camera, MapPin, X, Trash, CheckCircle, Loader2, UserCheck, User, AlertCircle } from 'lucide-react';
import { useWeekStartDay } from '../hooks/useWeekStartDay';

// Helper function to extract timezone-naive time from datetime string (moved outside component)
// Handles both T-separated (2025-12-18T09:00:00) and space-separated (2025-12-18 09:00:00.0000000) formats
const extractTimezoneNaiveTime = (datetimeString: string | null | undefined): string => {
  if (!datetimeString) return '';
  try {
    // Try T-separated format first (ISO 8601)
    let timePart = datetimeString.split('T')[1];
    if (!timePart) {
      // Try space-separated format (yyyy-MM-dd HH:mm:ss.fffffff)
      const spaceIndex = datetimeString.indexOf(' ');
      if (spaceIndex > 0) {
        timePart = datetimeString.substring(spaceIndex + 1);
      }
    }
    if (!timePart) return '';
    
    // Extract hours and minutes (ignore seconds and milliseconds)
    const [hours, minutes] = timePart.split(':');
    if (!hours || !minutes) return '';
    
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  } catch (error) {
    console.warn('Error extracting timezone-naive time:', error);
    return '';
  }
};

// Helper function to convert time string to minutes for sorting (moved outside component)
const timeToMinutes = (timeStr: string): number => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const Attendance: React.FC = () => {
  const navigate = useNavigate();
  const weekStartsOn = useWeekStartDay(); // Universal week start day setting
  const { selectedLocationId, locations } = useLocationContext();
  const [searchParams] = useSearchParams();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [attendanceCounts, setAttendanceCounts] = useState<Record<string, number>>({}); // Counts per date
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set()); // Start with all collapsed
  const [loading, setLoading] = useState(false);
  const [refreshingDates, setRefreshingDates] = useState<Set<string>>(new Set());
  const [adjustedValues, setAdjustedValues] = useState<Record<string, { adjustedClockIn?: string; adjustedClockOut?: string }>>({});
  const debounceTimeouts = useRef<Record<number, NodeJS.Timeout>>({});
  const [newAttendanceModal, setNewAttendanceModal] = useState<{ open: boolean; selectedDate?: string }>({ open: false });
  const [formData, setFormData] = useState({ employeeId: '', inTime: '', outTime: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successDialog, setSuccessDialog] = useState<{ open: boolean; message?: string }>({ open: false });
  const [deletedRecords, setDeletedRecords] = useState<Set<number>>(new Set());
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    recordId?: number;
    employeeName?: string;
    mode?: 'soft' | 'hard';
  }>({ open: false });
  const [savingRecords, setSavingRecords] = useState<Set<number>>(new Set());
  const [savedRecords, setSavedRecords] = useState<Set<number>>(new Set());
  const [photoModal, setPhotoModal] = useState<{ open: boolean; photo?: string; type?: 'clockIn' | 'clockOut' }>({ open: false });
  const [employeeSearchQueries, setEmployeeSearchQueries] = useState<Record<string, string>>({}); // Search query per date

  // Calculate week start and end using universal week start day setting
  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);

  // Generate array of dates for the week - memoized
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(weekStart, i));
    }
    return dates;
  }, [weekStart]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const data = await employeeApi.getAll();
        setEmployees(data);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // Track URL parameters for date expansion and scrolling
  const targetDateFromUrl = useRef<string | null>(null);
  const targetRecordIdFromUrl = useRef<number | null>(null);

  // Handle URL parameters for navigation (week, date, recordId)
  useEffect(() => {
    const weekParam = searchParams.get('week');
    const dateParam = searchParams.get('date');
    const recordIdParam = searchParams.get('recordId');
    
    if (weekParam) {
      try {
        const weekDate = parseISO(weekParam);
        if (!isNaN(weekDate.getTime())) {
          setCurrentWeek(weekDate);
        }
      } catch (error) {
        console.warn('Invalid week parameter:', weekParam);
      }
    }
    
    if (dateParam) {
      targetDateFromUrl.current = dateParam;
      if (recordIdParam) {
        const recordId = parseInt(recordIdParam, 10);
        if (!isNaN(recordId)) {
          targetRecordIdFromUrl.current = recordId;
        }
      }
    } else {
      targetDateFromUrl.current = null;
      targetRecordIdFromUrl.current = null;
    }
  }, [searchParams]);

  // Fetch attendance counts for the current week (not full records)
  // All days start collapsed, records are fetched only when a day is expanded
  useEffect(() => {
    // Wait for locations to be loaded before fetching attendance
    if (locations.length === 0) {
      return;
    }

    const fetchAttendanceCounts = async () => {
      setLoading(true);
      try {
        const params: any = {
          startDate: format(weekStart, 'yyyy-MM-dd'),
          endDate: format(weekEnd, 'yyyy-MM-dd')
        };
        if (selectedLocationId) {
          params.locationId = selectedLocationId;
        }
        
        const counts = await attendanceApi.getCounts(params);
        
        // Convert array to object for easy lookup
        const countsMap: Record<string, number> = {};
        counts.forEach(item => {
          countsMap[item.date] = item.count;
        });
        setAttendanceCounts(countsMap);
        
        // Clear attendance data on week change - records will be fetched when days are expanded
        setAttendanceData([]);
        
        // Clear loaded dates for the new week
        loadedDatesRef.current.clear();
        
        // Check if URL has a date parameter - if so, expand it after counts are loaded
        if (targetDateFromUrl.current) {
          // Expand the target date from URL after counts are loaded
          setExpandedDates(new Set([targetDateFromUrl.current]));
        } else {
          // Otherwise collapse all days when week changes
          setExpandedDates(new Set());
        }
      } catch (error) {
        console.error('Error fetching attendance counts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceCounts();
  }, [weekStart, weekEnd, selectedLocationId, locations.length]);
  
  // Track which dates have been loaded to avoid refetching
  const loadedDatesRef = useRef<Set<string>>(new Set());
  
  // Fetch records for a specific date when it's expanded
  useEffect(() => {
    if (expandedDates.size === 0) return;
    
    const fetchExpandedDates = async () => {
      // Convert Set to Array for iteration
      const expandedDatesArray = Array.from(expandedDates);
      for (const dateStr of expandedDatesArray) {
        // Check if we already have data for this date
        if (loadedDatesRef.current.has(dateStr)) {
          // Already loaded, skip fetch
          continue;
        }
        
        setRefreshingDates(prev => {
          if (prev.has(dateStr)) return prev;
          return new Set(prev).add(dateStr);
        });
        
        try {
          const params: any = {
            startDate: dateStr,
            endDate: dateStr
          };
          if (selectedLocationId) {
            params.locationId = selectedLocationId;
          }
          
          console.log(`[Attendance] Fetching records for expanded date ${dateStr}`);
          const dateData = await attendanceApi.getAll(params);
          
          // Mark this date as loaded
          loadedDatesRef.current.add(dateStr);
          
          // Merge with existing data
          setAttendanceData(prevData => {
            // Filter out any existing records for this date (in case of refresh)
            const filteredData = prevData.filter(record => {
              if (!record.clockInTime) return true;
              const recordDate = format(new Date(record.clockInTime), 'yyyy-MM-dd');
              return recordDate !== dateStr;
            });
            
            // Add the newly fetched records
            return [...filteredData, ...dateData];
          });
          
          // Update adjusted values for the newly fetched records
          setAdjustedValues(prev => {
            const newAdjustedValues = { ...prev };
            dateData.forEach((record: AttendanceRecord) => {
              // Always default AdjustedClockIn to ClockInTime if AdjustedClockIn is null/empty
              const adjustedIn = record.adjustedClockIn && record.adjustedClockIn.trim()
                ? extractTimezoneNaiveTime(record.adjustedClockIn)
                : extractTimezoneNaiveTime(record.clockInTime);
              
              // AdjustedClockOut defaults to ClockOutTime if not set
              const adjustedOut = record.adjustedClockOut && record.adjustedClockOut.trim()
                ? extractTimezoneNaiveTime(record.adjustedClockOut)
                : (record.clockOutTime ? extractTimezoneNaiveTime(record.clockOutTime) : '');
              
              newAdjustedValues[record.id] = {
                adjustedClockIn: adjustedIn,
                adjustedClockOut: adjustedOut
              };
            });
            return newAdjustedValues;
          });
          
          // Initialize deleted records
          setDeletedRecords(prev => {
            const newDeleted = new Set(prev);
            dateData.forEach((record: AttendanceRecord) => {
              if (record.deletedAt) {
                newDeleted.add(record.id);
              }
            });
            return newDeleted;
          });
          
          // After records are loaded, check if we need to scroll to a specific record
          if (targetDateFromUrl.current === dateStr && targetRecordIdFromUrl.current !== null) {
            // Wait a bit for the DOM to update, then scroll to the record
            setTimeout(() => {
              const recordId = targetRecordIdFromUrl.current!;
              const recordElement = document.getElementById(`attendance-record-${recordId}`);
              if (recordElement) {
                recordElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Highlight the record briefly
                recordElement.style.backgroundColor = '#fef3c7';
                setTimeout(() => {
                  recordElement.style.backgroundColor = '';
                }, 2000);
                console.log('[Attendance] Scrolled to record', recordId);
              } else {
                // Retry scrolling if element not found yet
                setTimeout(() => {
                  const retryElement = document.getElementById(`attendance-record-${recordId}`);
                  if (retryElement) {
                    retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    retryElement.style.backgroundColor = '#fef3c7';
                    setTimeout(() => {
                      retryElement.style.backgroundColor = '';
                    }, 2000);
                    console.log('[Attendance] Scrolled to record', recordId, 'on retry');
                  } else {
                    console.warn('[Attendance] Could not find record element', recordId);
                  }
                }, 500);
              }
              // Clear the target after scrolling
              targetRecordIdFromUrl.current = null;
            }, 300);
          }
        } catch (error) {
          console.error(`Error fetching records for date ${dateStr}:`, error);
        } finally {
          setRefreshingDates(prev => {
            const next = new Set(prev);
            next.delete(dateStr);
            return next;
          });
        }
      }
    };
    
    fetchExpandedDates();
  }, [expandedDates, selectedLocationId]);
  
  // Clear loaded dates when week changes
  useEffect(() => {
    loadedDatesRef.current.clear();
  }, [weekStart, weekEnd]);
  

  // Get employee display name - memoized with useMemo for employee lookup
  const employeeMap = useMemo(() => {
    const map = new Map<number, Employee>();
    employees.forEach(emp => map.set(emp.id, emp));
    return map;
  }, [employees]);

  const getEmployeeDisplayName = useCallback((record: AttendanceRecord) => {
    // First try to use employee data from the attendance record (most reliable)
    if (record.employee) {
      return `${record.employee.firstName} ${record.employee.lastName}`;
    }
    // Fallback to employeeMap
    const employee = employeeMap.get(record.employeeId);
    if (employee) {
      return `${employee.firstName} ${employee.lastName}`;
    }
    // Last resort
    return `Employee ${record.employeeId}`;
  }, [employeeMap]);

  // Memoize attendance records by date for performance
  const attendanceByDate = useMemo(() => {
    const map = new Map<string, AttendanceRecord[]>();
    // Build date map from attendance data
    attendanceData.forEach((record) => {
      if (!record.clockInTime) {
        // Skip records without clockInTime
        return;
      }
      // Parse the clockInTime - it should be in UTC from the backend
      const clockInUTC = new Date(record.clockInTime);
      const dateStr = format(clockInUTC, 'yyyy-MM-dd');
      if (!map.has(dateStr)) {
        map.set(dateStr, []);
      }
      map.get(dateStr)!.push(record);
    });
    
    
    // Sort each date's records
    map.forEach((records) => {
      records.sort((a, b) => {
        const timeA = extractTimezoneNaiveTime(a.clockInTime);
        const timeB = extractTimezoneNaiveTime(b.clockInTime);
        const minutesA = timeToMinutes(timeA);
        const minutesB = timeToMinutes(timeB);
        return minutesA - minutesB;
      });
    });
    
    return map;
  }, [attendanceData]);

  // Get attendance records for a specific date - memoized
  const getAttendanceForDate = useCallback((date: Date, searchQuery?: string) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    let records = attendanceByDate.get(dateStr) || [];
    
    // Filter by employee search query if provided
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      records = records.filter(record => {
        const employeeName = getEmployeeDisplayName(record).toLowerCase();
        return employeeName.includes(query);
      });
    }
    
    return records;
  }, [attendanceByDate, getEmployeeDisplayName]);

  // Count attendance records for a specific date - use counts from API or fallback to loaded records
  const getAttendanceCountForDate = useCallback((date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // First try to use the count from API (for collapsed days)
    if (attendanceCounts[dateStr] !== undefined) {
      return attendanceCounts[dateStr];
    }
    // Fallback to counting loaded records (for expanded dates that have been loaded)
    return getAttendanceForDate(date).length;
  }, [attendanceCounts, getAttendanceForDate]);

  // Toggle expanded state for a date - only one date expanded at a time.
  // This just manages UI state; actual data refresh is triggered from the click handler.
  const toggleDateExpansion = useCallback((dateStr: string) => {
    setExpandedDates(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(dateStr)) {
        newExpanded.delete(dateStr);
      } else {
        newExpanded.clear();
        newExpanded.add(dateStr);
      }
      return newExpanded;
    });
  }, []);

  // Update adjusted value and save with proper debouncing
  const updateAdjustedValue = useCallback((recordId: number, field: 'adjustedClockIn' | 'adjustedClockOut', value: string) => {
    // Update local state immediately
    setAdjustedValues(prev => ({
      ...prev,
      [recordId]: {
        ...prev[recordId],
        [field]: value
      }
    }));

    // Clear saved state when user starts typing again
    setSavedRecords(prev => {
      const newSet = new Set(prev);
      newSet.delete(recordId);
      return newSet;
    });

    // Clear existing timeout for this record
    if (debounceTimeouts.current[recordId]) {
      clearTimeout(debounceTimeouts.current[recordId]);
    }

    // Set new timeout to save after user stops typing
    debounceTimeouts.current[recordId] = setTimeout(async () => {
      // Mark as saving
      setSavingRecords(prev => new Set(prev).add(recordId));
      
      try {
        // Get current record from attendanceData
        const record = attendanceData.find(r => r.id === recordId);
        if (!record) {
          console.error('[Attendance] Record not found:', recordId);
          setSavingRecords(prev => {
            const newSet = new Set(prev);
            newSet.delete(recordId);
            return newSet;
          });
          delete debounceTimeouts.current[recordId];
          return;
        }

        // Get current adjusted values for this record
        const currentAdjustedValues = adjustedValues[recordId] || {};
        
        const updateData: any = {};
        
        // Always send both fields to prevent the backend from clearing the other field
        // Construct full datetime string from the record's date and the time value
        if (field === 'adjustedClockIn') {
          // Extract date from clockInTime (handle both T-separated and space-separated formats)
          const recordDate = record.clockInTime.includes('T') 
            ? record.clockInTime.split('T')[0]
            : record.clockInTime.split(' ')[0];
          
          if (value && value.trim()) {
            // User provided a value - use it
            updateData.adjustedClockIn = `${recordDate}T${value}:00`;
          } else {
            // If empty, default to ClockInTime (don't send null - backend will default it)
            // Extract time from ClockInTime and use that as the default
            const clockInTime = extractTimezoneNaiveTime(record.clockInTime);
            if (clockInTime) {
              updateData.adjustedClockIn = `${recordDate}T${clockInTime}:00`;
            } else {
              // Fallback: use the original ClockInTime value
              updateData.adjustedClockIn = record.clockInTime;
            }
          }
          // Always send adjustedClockOut to preserve it
          // Use current adjustedClockOut from state if available, otherwise use original clockOutTime
          if (currentAdjustedValues.adjustedClockOut && currentAdjustedValues.adjustedClockOut.trim()) {
            const outDate = record.clockOutTime 
              ? (record.clockOutTime.includes('T') ? record.clockOutTime.split('T')[0] : record.clockOutTime.split(' ')[0])
              : (record.clockInTime.includes('T') ? record.clockInTime.split('T')[0] : record.clockInTime.split(' ')[0]);
            updateData.adjustedClockOut = `${outDate}T${currentAdjustedValues.adjustedClockOut}:00`;
          } else {
            // Default to ClockOutTime if available
            updateData.adjustedClockOut = record.adjustedClockOut || record.clockOutTime || null;
          }
        } else {
          // Extract date from clockOutTime (handle both formats)
          const outDate = record.clockOutTime 
            ? (record.clockOutTime.includes('T') ? record.clockOutTime.split('T')[0] : record.clockOutTime.split(' ')[0])
            : (record.clockInTime.includes('T') ? record.clockInTime.split('T')[0] : record.clockInTime.split(' ')[0]);
          
          if (value && value.trim() && record.clockOutTime) {
            // User provided a value - use it
            updateData.adjustedClockOut = `${outDate}T${value}:00`;
          } else {
            // If empty, default to ClockOutTime (don't send null - backend will default it)
            const clockOutTime = record.clockOutTime ? extractTimezoneNaiveTime(record.clockOutTime) : '';
            if (clockOutTime) {
              updateData.adjustedClockOut = `${outDate}T${clockOutTime}:00`;
            } else {
              updateData.adjustedClockOut = record.adjustedClockOut || record.clockOutTime || null;
            }
          }
          // Always send adjustedClockIn to preserve it
          // Use current adjustedClockIn from state if available, otherwise use original clockInTime
          if (currentAdjustedValues.adjustedClockIn && currentAdjustedValues.adjustedClockIn.trim()) {
            const inDate = record.clockInTime.includes('T') 
              ? record.clockInTime.split('T')[0]
              : record.clockInTime.split(' ')[0];
            updateData.adjustedClockIn = `${inDate}T${currentAdjustedValues.adjustedClockIn}:00`;
          } else {
            // Default to ClockInTime
            updateData.adjustedClockIn = record.adjustedClockIn || record.clockInTime || null;
          }
        }
        
        console.log('[Attendance] Updating record', recordId, 'with data:', updateData, 'original value:', value);
        console.log('[Attendance] Record before update:', record);
        console.log('[Attendance] Current adjusted values:', currentAdjustedValues);
        
        await attendanceApi.update(recordId, updateData);
        console.log('[Attendance] Update API call successful');
        
        // Refresh the attendance data to get the updated values from the server
        const params: any = {
          startDate: format(weekStart, 'yyyy-MM-dd'),
          endDate: format(weekEnd, 'yyyy-MM-dd')
        };
        if (selectedLocationId) {
          params.locationId = selectedLocationId;
        }
        const data = await attendanceApi.getAll(params);
        const updatedRecord = data.find((r: AttendanceRecord) => r.id === recordId);
        console.log('[Attendance] Refreshed data after update - full record:', updatedRecord);
        console.log('[Attendance] Updated record adjustedClockIn (raw):', updatedRecord?.adjustedClockIn);
        console.log('[Attendance] Updated record adjustedClockOut (raw):', updatedRecord?.adjustedClockOut);
        
        setAttendanceData(data);
        
        // Update adjusted values with the fresh data from server
        // AdjustedClockIn should always default to ClockInTime if not set
        // AdjustedClockOut should default to ClockOutTime if not set
        const newAdjustedValues: Record<string, { adjustedClockIn?: string; adjustedClockOut?: string }> = {};
        data.forEach((record: AttendanceRecord) => {
          // Always default AdjustedClockIn to ClockInTime if AdjustedClockIn is null/empty
          const adjustedIn = record.adjustedClockIn && record.adjustedClockIn.trim()
            ? extractTimezoneNaiveTime(record.adjustedClockIn)
            : extractTimezoneNaiveTime(record.clockInTime);
          
          // AdjustedClockOut defaults to ClockOutTime if not set
          const adjustedOut = record.adjustedClockOut && record.adjustedClockOut.trim()
            ? extractTimezoneNaiveTime(record.adjustedClockOut)
            : (record.clockOutTime ? extractTimezoneNaiveTime(record.clockOutTime) : '');
          
          newAdjustedValues[record.id] = {
            adjustedClockIn: adjustedIn,
            adjustedClockOut: adjustedOut
          };
        });
        console.log('[Attendance] New adjusted values for updated record:', newAdjustedValues[recordId]);
        setAdjustedValues(newAdjustedValues);
        
        // Remove from saving, add to saved
        setSavingRecords(prev => {
          const newSet = new Set(prev);
          newSet.delete(recordId);
          return newSet;
        });
        setSavedRecords(prev => new Set(prev).add(recordId));
        
        // Remove saved indicator after 2 seconds
        setTimeout(() => {
          setSavedRecords(prev => {
            const newSet = new Set(prev);
            newSet.delete(recordId);
            return newSet;
          });
        }, 2000);
        
        delete debounceTimeouts.current[recordId];
      } catch (error) {
        console.error('Error updating attendance:', error);
        setSavingRecords(prev => {
          const newSet = new Set(prev);
          newSet.delete(recordId);
          return newSet;
        });
        delete debounceTimeouts.current[recordId];
      }
    }, 1000);
  }, [weekStart, weekEnd, selectedLocationId, attendanceData, adjustedValues]);

  // Calculate total hours - memoized per record
  const calculateTotalHours = useCallback((record: AttendanceRecord, adjustedClockIn?: string, adjustedClockOut?: string) => {
    if (!adjustedClockIn || !adjustedClockOut) return '0.00';
    
    const today = format(new Date(), 'yyyy-MM-dd');
    const clockInTime = new Date(`${today}T${adjustedClockIn}:00`);
    let clockOutTime = new Date(`${today}T${adjustedClockOut}:00`);
    
    // Only treat as overnight if clock-out is strictly BEFORE clock-in.
    // If they are exactly equal, duration should be 0.00 hours, not 24.00.
    if (clockOutTime < clockInTime) {
      clockOutTime.setDate(clockOutTime.getDate() + 1);
    }
    
    const diffMs = clockOutTime.getTime() - clockInTime.getTime();
    const hours = (diffMs / (1000 * 60 * 60)).toFixed(2);
    return hours;
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Refresh date data - memoized - only fetches data for the specific date
  const refreshDateData = useCallback(async (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    setRefreshingDates(prev => {
      if (prev.has(dateStr)) return prev;
      return new Set(prev).add(dateStr);
    });

    try {
      // Fetch only data for the specific date
      const params: any = {
        startDate: dateStr,
        endDate: dateStr
      };
      if (selectedLocationId) {
        params.locationId = selectedLocationId;
      }
      
      console.log(`[Attendance] Refreshing data for date ${dateStr} only`);
      const [dateData, counts] = await Promise.all([
        attendanceApi.getAll(params),
        attendanceApi.getCounts(params)
      ]);
      
      // Update count for this date
      const countForDate = counts.find(c => c.date === dateStr)?.count || 0;
      setAttendanceCounts(prev => ({
        ...prev,
        [dateStr]: countForDate
      }));
      
      // Mark as loaded (or reloaded) - remove first to allow refetch
      loadedDatesRef.current.delete(dateStr);
      loadedDatesRef.current.add(dateStr);
      
      // Merge with existing data: remove old records for this date and add new ones
      setAttendanceData(prevData => {
        // Filter out records that belong to this date
        const filteredData = prevData.filter(record => {
          if (!record.clockInTime) return true;
          const recordDate = format(new Date(record.clockInTime), 'yyyy-MM-dd');
          return recordDate !== dateStr;
        });
        
        // Add the newly fetched records for this date
        const mergedData = [...filteredData, ...dateData];
        
        console.log(`[Attendance] Merged data: removed ${prevData.length - filteredData.length} old records, added ${dateData.length} new records for ${dateStr}`);
        
        return mergedData;
      });
      
      // Update adjusted values for the newly fetched records
      setAdjustedValues(prev => {
        const newAdjustedValues = { ...prev };
        dateData.forEach((record: AttendanceRecord) => {
          // Always default AdjustedClockIn to ClockInTime if AdjustedClockIn is null/empty
          const adjustedIn = record.adjustedClockIn && record.adjustedClockIn.trim()
            ? extractTimezoneNaiveTime(record.adjustedClockIn)
            : extractTimezoneNaiveTime(record.clockInTime);
          
          // AdjustedClockOut defaults to ClockOutTime if not set
          const adjustedOut = record.adjustedClockOut && record.adjustedClockOut.trim()
            ? extractTimezoneNaiveTime(record.adjustedClockOut)
            : (record.clockOutTime ? extractTimezoneNaiveTime(record.clockOutTime) : '');
          
          newAdjustedValues[record.id] = {
            adjustedClockIn: adjustedIn,
            adjustedClockOut: adjustedOut
          };
        });
        return newAdjustedValues;
      });
    } catch (error) {
      console.error('Error refreshing attendance:', error);
    } finally {
      setRefreshingDates(prev => {
        const newSet = new Set(prev);
        newSet.delete(dateStr);
        return newSet;
      });
    }
  }, [selectedLocationId]);

  // Navigation handlers - memoized
  const goToPreviousWeek = useCallback(() => {
    setCurrentWeek(prev => subWeeks(prev, 1));
  }, []);

  const goToNextWeek = useCallback(() => {
    setCurrentWeek(prev => addWeeks(prev, 1));
  }, []);

  const goToCurrentWeek = useCallback(() => {
    setCurrentWeek(new Date());
  }, []);

  // Open new attendance modal
  const openNewAttendanceModal = useCallback((date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setNewAttendanceModal({ open: true, selectedDate: dateStr });
    setFormData({ employeeId: '', inTime: '', outTime: '' });
  }, []);

  // Close new attendance modal
  const closeNewAttendanceModal = useCallback(() => {
    setNewAttendanceModal({ open: false });
    setFormData({ employeeId: '', inTime: '', outTime: '' });
  }, []);

  // Handle form submission
  const handleCreateAttendance = useCallback(async () => {
    if (!formData.employeeId || !formData.inTime) {
      alert('Please fill in all required fields (Employee and In Time)');
      return;
    }

    if (!newAttendanceModal.selectedDate) {
      alert('No date selected');
      return;
    }

    setIsSubmitting(true);
    try {
      // Format date/time as ISO string without timezone (backend expects this format)
      const clockIn = `${newAttendanceModal.selectedDate}T${formData.inTime}:00`;
      const clockOut = formData.outTime ? `${newAttendanceModal.selectedDate}T${formData.outTime}:00` : undefined;

      console.log('Creating attendance with:', { 
        clockIn, 
        clockOut, 
        employeeId: formData.employeeId,
        selectedLocationId,
        locationId: selectedLocationId || undefined
      });

      if (!selectedLocationId) {
        alert('Please select a location from the dropdown at the top of the page before creating attendance.');
        setIsSubmitting(false);
        return;
      }

      await attendanceApi.create({
        employeeId: parseInt(formData.employeeId),
        clockIn: clockIn,
        clockOut: clockOut,
        timeStationId: undefined,
        locationId: selectedLocationId, // Always use selectedLocationId - it's required
        isManualClockIn: true,
        isManualClockOut: !!formData.outTime
      });

      // Close modal first
      closeNewAttendanceModal();

      // Refresh counts for the week
      const params: any = {
        startDate: format(weekStart, 'yyyy-MM-dd'),
        endDate: format(weekEnd, 'yyyy-MM-dd')
      };
      if (selectedLocationId) {
        params.locationId = selectedLocationId;
      }
      
      // Refresh counts
      const counts = await attendanceApi.getCounts(params);
      const countsMap: Record<string, number> = {};
      counts.forEach(item => {
        countsMap[item.date] = item.count;
      });
      setAttendanceCounts(countsMap);

      // Auto-expand the date where the record was added and fetch its records
      if (newAttendanceModal.selectedDate) {
        setExpandedDates(prev => new Set(prev).add(newAttendanceModal.selectedDate!));
        // Records will be fetched automatically by the useEffect that watches expandedDates
      }

      setSuccessDialog({ open: true, message: 'Attendance record created successfully!' });
    } catch (error: any) {
      console.error('Error creating attendance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create attendance record';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, newAttendanceModal.selectedDate, selectedLocationId, weekStart, weekEnd, closeNewAttendanceModal]);

  // Fetch today's attendance records for absent alerts
  const [todayAttendanceData, setTodayAttendanceData] = useState<AttendanceRecord[]>([]);
  // Fetch today's scheduled shifts to check if employees are scheduled
  const [todayScheduledShifts, setTodayScheduledShifts] = useState<any[]>([]);
  // Fetch all currently clocked-in records (for missed clockout alerts) - not limited to expanded dates
  const [currentClockInRecords, setCurrentClockInRecords] = useState<AttendanceRecord[]>([]);
  
  useEffect(() => {
    // Fetch all currently clocked-in records (for missed clockout alerts)
    const fetchCurrentClockInRecords = async () => {
      try {
        // Fetch records from last 30 days to cover all possible clocked-in records
        // Filter for records that have clockInTime but no clockOutTime
        const thirtyDaysAgo = format(addDays(new Date(), -30), 'yyyy-MM-dd');
        const today = format(new Date(), 'yyyy-MM-dd');
        
        const params: any = {
          startDate: thirtyDaysAgo,
          endDate: today
        };
        if (selectedLocationId) {
          params.locationId = selectedLocationId;
        }
        
        const allRecords = await attendanceApi.getAll(params);
        // Filter for records that are currently clocked in (have clockInTime but no clockOutTime)
        const clockedInRecords = allRecords.filter(
          record => record.clockInTime && !record.clockOutTime && !record.deletedAt
        );
        setCurrentClockInRecords(clockedInRecords);
      } catch (error) {
        console.error('Error fetching current clock-in records:', error);
      }
    };

    if (isToday(new Date())) {
      const fetchTodayData = async () => {
        try {
          const today = format(new Date(), 'yyyy-MM-dd');
          
          // Fetch today's attendance
          const attendanceParams: any = {
            startDate: today,
            endDate: today
          };
          if (selectedLocationId) {
            attendanceParams.locationId = selectedLocationId;
          }
          
          const todayData = await attendanceApi.getAll(attendanceParams);
          setTodayAttendanceData(todayData);
          
          // Fetch today's scheduled shifts
          const shiftParams: any = {
            startDate: today,
            endDate: today
          };
          if (selectedLocationId) {
            shiftParams.locationId = selectedLocationId;
          }
          
          const todayShifts = await shiftApi.getAll(shiftParams);
          setTodayScheduledShifts(todayShifts);
        } catch (error) {
          console.error('Error fetching today data:', error);
        }
      };

      fetchTodayData();
    }

    fetchCurrentClockInRecords();
    // Refresh every 5 minutes to keep alerts updated
    const interval = setInterval(() => {
      if (isToday(new Date())) {
        const fetchTodayData = async () => {
          try {
            const today = format(new Date(), 'yyyy-MM-dd');
            
            const attendanceParams: any = {
              startDate: today,
              endDate: today
            };
            if (selectedLocationId) {
              attendanceParams.locationId = selectedLocationId;
            }
            
            const todayData = await attendanceApi.getAll(attendanceParams);
            setTodayAttendanceData(todayData);
            
            const shiftParams: any = {
              startDate: today,
              endDate: today
            };
            if (selectedLocationId) {
              shiftParams.locationId = selectedLocationId;
            }
            
            const todayShifts = await shiftApi.getAll(shiftParams);
            setTodayScheduledShifts(todayShifts);
          } catch (error) {
            console.error('Error fetching today data:', error);
          }
        };
        fetchTodayData();
      }
      fetchCurrentClockInRecords();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [selectedLocationId]);

  // Helper function to calculate hours since clock in (matching backup logic)
  const calculateHoursSinceClockIn = useCallback((clockInTime: string | null | undefined) => {
    if (!clockInTime) return { formatted: '0:00', totalHours: 0 };
    
    const clockIn = new Date(clockInTime);
    const now = new Date();
    const totalMinutes = differenceInMinutes(now, clockIn);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return {
      formatted: `${hours}:${minutes.toString().padStart(2, '0')}`,
      totalHours: hours
    };
  }, []);

  // Calculate alerts for absent and missed clockout (matching backup logic exactly)
  const alerts = useMemo(() => {
    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');
    const absentEmployees: Array<{ employee: Employee; date: string; shift: any }> = [];
    const missedClockoutEmployees: Array<{ employee: Employee; record: AttendanceRecord; date: string; hoursClocked: string }> = [];

    // Map attendance by employeeId for quick lookup (only records without clockOutTime)
    // Use currentClockInRecords which contains ALL currently clocked-in employees (not just expanded dates)
    const attendanceMap = new Map<number, AttendanceRecord>();
    currentClockInRecords.forEach((record) => {
      // Only include records that are currently clocked in (have clockInTime but no clockOutTime)
      if (record.clockInTime && !record.clockOutTime && !record.deletedAt) {
        const existing = attendanceMap.get(record.employeeId);
        // Keep the most recent record if there are duplicates
        if (!existing || new Date(record.clockInTime) > new Date(existing.clockInTime)) {
          attendanceMap.set(record.employeeId, record);
        }
      }
    });
    
    // Also merge with today's attendance data for absent check
    const todayClockIns = new Map<number, AttendanceRecord>();
    [...attendanceData, ...todayAttendanceData].forEach((record) => {
      if (record.clockInTime && !record.clockOutTime && !record.deletedAt) {
        const existing = todayClockIns.get(record.employeeId);
        if (!existing || new Date(record.clockInTime) > new Date(existing.clockInTime)) {
          todayClockIns.set(record.employeeId, record);
        }
      }
    });

    // Check for missed clockouts - employees who clocked in but didn't clock out for 24+ hours
    // (matching backup logic: hoursClocked.totalHours >= 24)
    // Only show employees who have been clocked in for 24+ hours without clocking out
    attendanceMap.forEach((record, employeeId) => {
      // Calculate hours since clock in - must be 24+ hours
      const hoursClocked = calculateHoursSinceClockIn(record.clockInTime);
      
      // CRITICAL: Only show if it's been 24+ hours (1440+ minutes) since clock in
      // This ensures we only show employees who have missed clockout, not those who are still within 24 hours
      if (hoursClocked.totalHours >= 24) {
        // Always use full Employee object from employees array
        const employee = employees.find(e => e.id === record.employeeId);
        
        if (employee) {
          const recordDate = format(new Date(record.clockInTime), 'yyyy-MM-dd');
          missedClockoutEmployees.push({
            employee,
            record,
            date: recordDate,
            hoursClocked: hoursClocked.formatted
          });
        }
      }
    });

    // Check for absent employees - those who were scheduled but didn't clock in
    // Only check today since we can't determine if someone is "absent" for future dates
    if (isToday(now)) {
      // Map employees by ID for name lookup
      const employeeMap = new Map<number, Employee>();
      employees.forEach((emp) => {
        employeeMap.set(emp.id, emp);
      });

      // Process today's shifts to find absent employees (matching backup logic)
      todayScheduledShifts.forEach((shift: any) => {
        const employeeId = shift.employeeId || (shift.userId ? parseInt(shift.userId, 10) : null);
        if (!employeeId) return;

        // Skip if location filter doesn't match
        if (selectedLocationId && shift.locationId !== selectedLocationId) {
          return;
        }

        const employee = employeeMap.get(employeeId);
        if (!employee) return;

        // Check if this employee has clocked in today (use todayClockIns for today's check)
        const attendance = todayClockIns.get(employeeId);
        
        if (!attendance) {
          // Employee has NOT clocked in - check if it's been 60+ minutes since shift start
          const shiftStart = new Date(shift.startTime);
          const minutesSinceStart = differenceInMinutes(now, shiftStart);
          
          // Absent: 1 hour or more past scheduled start (matching backup logic: minutesSinceStart >= 60)
          if (minutesSinceStart >= 60) {
            absentEmployees.push({
              employee,
              date: today,
              shift
            });
          }
        }
      });
    }

    return {
      absent: absentEmployees,
      missedClockout: missedClockoutEmployees
    };
  }, [currentClockInRecords, attendanceData, todayAttendanceData, todayScheduledShifts, employees, selectedLocationId, calculateHoursSinceClockIn]);

  if (loading && Object.keys(attendanceCounts).length === 0) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading attendance counts...</div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar - Alerts */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
          {/* Absent Alerts */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <h3 className="font-medium text-gray-900">Absent Today</h3>
              {alerts.absent.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                  {alerts.absent.length}
                </span>
              )}
            </div>
            {alerts.absent.length > 0 ? (
              <div className="space-y-2">
                {alerts.absent.map(({ employee, date }) => (
                  <div
                    key={`absent-${employee.id}-${date}`}
                    className="p-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                  >
                    <div 
                      className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                      onClick={() => window.open(`/employees/${employee.id}?tab=notes`, '_blank')}
                      title="View employee notes"
                    >
                      {employee.firstName} {employee.lastName}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {format(new Date(date), 'MMM d, yyyy')}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-2">No absent employees today</div>
            )}
          </div>

          {/* Missed Clockout Alerts */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-5 w-5 text-red-600" />
              <h3 className="font-medium text-gray-900">Missed Clockout</h3>
              {alerts.missedClockout.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                  {alerts.missedClockout.length}
                </span>
              )}
            </div>
            {alerts.missedClockout.length > 0 ? (
              <div className="space-y-2">
                {alerts.missedClockout.map(({ employee, record, date, hoursClocked }) => {
                  const clockInTime = extractTimezoneNaiveTime(record.clockInTime);
                  return (
                    <div
                      key={`missed-${record.id}`}
                      className="p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <div className="font-medium text-gray-900">
                        {employee.firstName} {employee.lastName}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        Clocked in: {clockInTime} on {format(new Date(date), 'MMM d, yyyy')}
                      </div>
                      <div className="text-xs text-red-700 font-medium mt-1">
                        Hours clocked: {hoursClocked} (24+ hours - missed clock out)
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-2">No missed clockouts</div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Attendance</h1>
      
      {/* Week Navigator */}
      <div className="flex items-center justify-center mb-6 space-x-4">
        <button
          onClick={goToPreviousWeek}
          className="p-2 border rounded hover:bg-gray-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        
        <div className="text-lg font-medium">
          {format(weekStart, "MMM d")} - {format(weekEnd, "MMM d, yyyy")}
        </div>
        
        <button
          onClick={goToNextWeek}
          className="p-2 border rounded hover:bg-gray-100"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        
        <button
          onClick={goToCurrentWeek}
          disabled={isSameWeek(currentWeek, new Date(), { weekStartsOn: 4 })}
          className="px-4 py-2 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
        >
          Current week
        </button>
      </div>
      
      {/* Week View */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Weekly Attendance Overview</h2>
        </div>
        <div className="p-4 space-y-2">
          {weekDates.map((date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const attendanceCount = getAttendanceCountForDate(date);
            const searchQuery = employeeSearchQueries[dateStr] || '';
            const attendanceRecords = getAttendanceForDate(date, searchQuery);
            const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
            const isExpanded = expandedDates.has(dateStr);
            
            console.log('[Attendance] Rendering date:', dateStr, 'count:', attendanceCount, 'records:', attendanceRecords.length, 'expanded:', isExpanded);
            
            return (
              <div key={dateStr} className="border rounded-lg">
                <div
                  className={`w-full flex items-center justify-between p-4 rounded-lg border transition-colors ${
                    isToday ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'
                  }`}
                >
                  <div
                    onClick={() => {
                      // Toggle expansion - records will be fetched automatically by useEffect
                      toggleDateExpansion(dateStr);
                    }}
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                  >
                    <div className="flex items-center justify-center w-5 h-5">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {format(date, 'EEEE, MMM d')}
                      </span>
                      <span className="text-sm text-gray-500">
                        {format(date, 'yyyy')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshDateData(date);
                      }}
                      disabled={refreshingDates.has(dateStr)}
                      className="text-xs px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 flex items-center gap-1"
                    >
                      <RefreshCw className={`h-3 w-3 ${refreshingDates.has(dateStr) ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openNewAttendanceModal(date);
                      }}
                      className="text-xs px-2 py-1 border rounded hover:bg-gray-100 flex items-center gap-1 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                    >
                      <Plus className="h-3 w-3" />
                      Add
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Records:
                      </span>
                      <span className={`font-bold text-lg ${
                        attendanceCount > 0 ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {attendanceCount}
                      </span>
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4" style={{ minHeight: attendanceRecords.length > 0 ? '200px' : 'auto' }}>
                    {/* Employee Search Box */}
                    <div className="mt-4 mb-4">
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={employeeSearchQueries[dateStr] || ''}
                        onChange={(e) => {
                          setEmployeeSearchQueries(prev => ({
                            ...prev,
                            [dateStr]: e.target.value
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    {(() => {
                      console.log('[Attendance] Expanded section rendering for', dateStr, 'with', attendanceRecords.length, 'records');
                      return null;
                    })()}
                    {attendanceRecords.length > 0 ? (
                      <div className="mt-4 overflow-x-auto">
                        <style>{`
                          /* For soft-deleted rows that belong to an employee with only one row (no grouping),
                             draw the strike-through across the entire row including the Employee column. */
                          tr.deleted-row.single-row-employee td {
                            position: relative;
                          }
                          tr.deleted-row.single-row-employee td::after {
                            content: '';
                            position: absolute;
                            left: 0;
                            right: 0;
                            top: 50%;
                            height: 2px;
                            background-color: #dc2626;
                            pointer-events: none;
                          }

                          /* For soft-deleted rows that are part of a multi-row group for the same employee,
                             start the strike-through from the In column (skip Employee column). */
                          tr.deleted-row.multi-row-employee td:not(:first-child) {
                            position: relative;
                          }
                          tr.deleted-row.multi-row-employee td:not(:first-child)::after {
                            content: '';
                            position: absolute;
                            left: 0;
                            right: 0;
                            top: 50%;
                            height: 2px;
                            background-color: #dc2626;
                            pointer-events: none;
                          }
                        `}</style>
                        {(() => {
                          // Debug: Log when table is rendered
                          console.log('[Attendance] Table rendered for date:', dateStr);
                          return null;
                        })()}
                        <table 
                          className="w-full caption-bottom text-sm attendance-table"
                        >
                          <thead>
                            <tr className="border-b">
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Employee</th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">In</th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Adjusted In</th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Out</th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Adjusted Out</th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Total Hours</th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // Group records by employee for this date so the employee name appears only once
                              const groups = new Map<number, AttendanceRecord[]>();
                              for (const rec of attendanceRecords) {
                                const key = rec.employeeId;
                                if (!groups.has(key)) {
                                  groups.set(key, []);
                                }
                                groups.get(key)!.push(rec);
                              }

                              const rows: React.ReactNode[] = [];

                              Array.from(groups.values()).forEach((recordsForEmployee) => {
                                const isMultiRowEmployee = recordsForEmployee.length > 1;

                                recordsForEmployee.forEach((record, index) => {
                                  console.log('[Attendance] Rendering record:', record.id, 'for date:', dateStr);
                                  const isDeleted = record.deletedAt != null || deletedRecords.has(record.id);
                                  const isFirstRowForEmployee = index === 0;

                                  rows.push(
                                    <tr
                                      key={record.id}
                                      id={`attendance-record-${record.id}`}
                                      className={`border-b transition-colors hover:bg-muted/50 ${
                                        isDeleted ? `opacity-60 deleted-row ${isMultiRowEmployee ? 'multi-row-employee' : 'single-row-employee'}` : ''
                                      }`}
                                    >
                                      {isFirstRowForEmployee && (
                                        <td
                                          className="p-4 align-middle font-medium relative"
                                          rowSpan={recordsForEmployee.length}
                                        >
                                          {getEmployeeDisplayName(record)}
                                        </td>
                                      )}
                                      <td className="p-4 align-middle relative">
                                        <div className="flex items-center gap-2">
                                          {extractTimezoneNaiveTime(record.clockInTime)}
                                          {record.clockInSource && (
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${
                                              record.clockInSource === 'timestation' ? 'bg-blue-500' :
                                              record.clockInSource === 'mobile' ? 'bg-green-500' :
                                              'bg-orange-500'
                                            }`}>
                                              {record.clockInSource === 'timestation' ? 'TS' : 
                                               record.clockInSource === 'mobile' ? 'MO' : 
                                               record.clockInSource === 'manual' ? 'MN' : record.clockInSource}
                                            </span>
                                          )}
                                          {record.clockInPhoto && (
                                            <button
                                              onClick={() => setPhotoModal({ open: true, photo: record.clockInPhoto, type: 'clockIn' })}
                                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                                              title="View Clock In Photo"
                                            >
                                              <Camera className="h-4 w-4 text-blue-600" />
                                            </button>
                                          )}
                                          {record.clockInPhoto && (
                                            record.faceMatchConfidence != null && record.faceMatchConfidence > 0 ? (
                                              <span title={`Face Verified (${record.faceMatchConfidence.toFixed(1)}%)`}>
                                                <UserCheck className="h-4 w-4 text-green-600" />
                                              </span>
                                            ) : (
                                              <div className="relative inline-block" title="Face Not Verified">
                                                <User className="h-4 w-4 text-red-600" />
                                                <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center pointer-events-none">
                                                  <div className="w-5 h-0.5 bg-red-600 transform rotate-45 origin-center"></div>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </td>
                                      <td className="p-4 align-middle">
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="time"
                                            className="w-24 h-8"
                                            value={adjustedValues[record.id]?.adjustedClockIn || ''}
                                            onChange={(e) =>
                                              updateAdjustedValue(record.id, 'adjustedClockIn', e.target.value)
                                            }
                                            disabled={isDeleted}
                                          />
                                          {savingRecords.has(record.id) && (
                                            <span className="text-xs text-blue-600 flex items-center gap-1">
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                              Saving...
                                            </span>
                                          )}
                                          {savedRecords.has(record.id) && !savingRecords.has(record.id) && (
                                            <span className="text-xs text-green-600 flex items-center gap-1">
                                              <CheckCircle className="h-3 w-3" />
                                              Saved
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="p-4 align-middle">
                                        <div className="flex items-center gap-2">
                                          {record.clockOutTime
                                            ? extractTimezoneNaiveTime(record.clockOutTime)
                                            : 'Not clocked out'}
                                          {record.clockOutSource && (
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${
                                              record.clockOutSource === 'timestation' ? 'bg-blue-500' :
                                              record.clockOutSource === 'mobile' ? 'bg-green-500' :
                                              'bg-orange-500'
                                            }`}>
                                              {record.clockOutSource === 'timestation' ? 'TS' : 
                                               record.clockOutSource === 'mobile' ? 'MO' : 
                                               record.clockOutSource === 'manual' ? 'MN' : record.clockOutSource}
                                            </span>
                                          )}
                                          {record.clockOutPhoto && (
                                            <button
                                              onClick={() => setPhotoModal({ open: true, photo: record.clockOutPhoto, type: 'clockOut' })}
                                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                                              title="View Clock Out Photo"
                                            >
                                              <Camera className="h-4 w-4 text-blue-600" />
                                            </button>
                                          )}
                                          {record.clockOutPhoto && (
                                            record.clockOutFaceMatchConfidence != null && record.clockOutFaceMatchConfidence > 0 ? (
                                              <span title={`Face Verified (${record.clockOutFaceMatchConfidence.toFixed(1)}%)`}>
                                                <UserCheck className="h-4 w-4 text-green-600" />
                                              </span>
                                            ) : (
                                              <div className="relative inline-block" title="Face Not Verified">
                                                <User className="h-4 w-4 text-red-600" />
                                                <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center pointer-events-none">
                                                  <div className="w-5 h-0.5 bg-red-600 transform rotate-45 origin-center"></div>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </td>
                                      <td className="p-4 align-middle">
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="time"
                                            className="w-24 h-8"
                                            value={adjustedValues[record.id]?.adjustedClockOut || ''}
                                            onChange={(e) =>
                                              updateAdjustedValue(record.id, 'adjustedClockOut', e.target.value)
                                            }
                                            disabled={isDeleted}
                                          />
                                          {savingRecords.has(record.id) && (
                                            <span className="text-xs text-blue-600 flex items-center gap-1">
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                              Saving...
                                            </span>
                                          )}
                                          {savedRecords.has(record.id) && !savingRecords.has(record.id) && (
                                            <span className="text-xs text-green-600 flex items-center gap-1">
                                              <CheckCircle className="h-3 w-3" />
                                              Saved
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="p-4 align-middle">
                                        <span className="font-medium">
                                          {calculateTotalHours(
                                            record,
                                            adjustedValues[record.id]?.adjustedClockIn,
                                            adjustedValues[record.id]?.adjustedClockOut
                                          )}
                                        </span>
                                      </td>
                                      <td className="p-4 align-middle">
                                        <div className="flex items-center gap-2">
                                          {/* Soft delete (mark as deleted / strike-through) */}
                                          <button
                                            onClick={() => {
                                              const employeeName = getEmployeeDisplayName(record);
                                              setDeleteConfirmDialog({
                                                open: true,
                                                recordId: record.id,
                                                employeeName,
                                                mode: 'soft',
                                              });
                                            }}
                                            className="text-red-600 hover:text-red-800 transition-colors p-1 rounded hover:bg-red-50"
                                            title="Soft delete record"
                                            disabled={isDeleted}
                                          >
                                            <X className="h-4 w-4" />
                                          </button>

                                          {/* Hard delete (permanently remove from table/database) */}
                                          <button
                                            onClick={() => {
                                              const employeeName = getEmployeeDisplayName(record);
                                              setDeleteConfirmDialog({
                                                open: true,
                                                recordId: record.id,
                                                employeeName,
                                                mode: 'hard',
                                              });
                                            }}
                                            className="text-red-600 hover:text-red-800 transition-colors p-1 rounded hover:bg-red-50"
                                            title="Hard delete record"
                                          >
                                            <Trash className="h-4 w-4" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                });
                              });

                              return rows;
                            })()}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-gray-500 text-center py-4">
                        No attendance records for this date
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
        </div>

      {/* New Attendance Modal */}
      {newAttendanceModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">New Attendance Record</h2>
              <button
                onClick={closeNewAttendanceModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Date Display */}
              {newAttendanceModal.selectedDate && (
                <div className="text-sm text-gray-600 mb-4">
                  <strong>Date:</strong> {format(new Date(newAttendanceModal.selectedDate), 'MMM dd, yyyy')}
                </div>
              )}

              {/* Employee Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.employeeId}
                  onChange={(e) => setFormData(prev => ({ ...prev, employeeId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select employee</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* In Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  In Time <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="time"
                    value={formData.inTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, inTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="--:--"
                  />
                  <Clock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Out Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Out Time <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="time"
                    value={formData.outTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, outTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="--:--"
                  />
                  <Clock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t">
              <button
                onClick={closeNewAttendanceModal}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAttendance}
                disabled={isSubmitting || !formData.employeeId || !formData.inTime}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Create Attendance'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      {successDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSuccessDialog({ open: false })}>
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full mx-4 transform transition-all" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Success!</h3>
              <p className="text-sm text-gray-600 mb-6">
                {successDialog.message || 'Attendance record created successfully!'}
              </p>
              <button
                onClick={() => setSuccessDialog({ open: false })}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setDeleteConfirmDialog({ open: false })}>
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full mx-4 transform transition-all" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <X className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">Delete Attendance Record?</h3>
              <p className="text-sm text-gray-600 mb-6 text-center">
                Are you sure you want to {deleteConfirmDialog.mode === 'hard' ? 'permanently delete' : 'soft delete'} the attendance record for <strong>{deleteConfirmDialog.employeeName}</strong>?
                {deleteConfirmDialog.mode === 'hard'
                  ? ' This action cannot be undone and the record will be removed from the table.'
                  : ' The record will be marked as deleted and shown with a strikethrough.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirmDialog({ open: false })}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!deleteConfirmDialog.recordId) return;
                    const id = deleteConfirmDialog.recordId;
                    const isHard = deleteConfirmDialog.mode === 'hard';

                    try {
                      // Find the record to get its date
                      const record = attendanceData.find(r => r.id === id);
                      const recordDate = record?.clockInTime ? format(new Date(record.clockInTime), 'yyyy-MM-dd') : null;
                      
                      await attendanceApi.delete(id, isHard);

                      if (isHard) {
                        // Hard delete: remove from table and clear any soft-delete state
                        setAttendanceData(prev => prev.filter(r => r.id !== id));
                        setDeletedRecords(prev => {
                          const next = new Set(prev);
                          next.delete(id);
                          return next;
                        });
                        
                        // Update count for the date
                        if (recordDate) {
                          setAttendanceCounts(prev => ({
                            ...prev,
                            [recordDate]: Math.max(0, (prev[recordDate] || 0) - 1)
                          }));
                          // Clear loaded flag so it can be refetched if needed
                          loadedDatesRef.current.delete(recordDate);
                        }
                      } else {
                        // Soft delete: keep record but mark as deleted for strikethrough
                        setDeletedRecords(prev => new Set(prev).add(id));
                        // Counts don't change for soft delete
                      }

                      setDeleteConfirmDialog({ open: false });
                    } catch (error: any) {
                      console.error('Error deleting attendance:', error);
                      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete attendance record';
                      alert(`Error: ${errorMessage}`);
                    }
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {photoModal.open && photoModal.photo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setPhotoModal({ open: false })}>
          <div className="bg-white rounded-lg p-4 max-w-2xl w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {photoModal.type === 'clockIn' ? 'Clock In Photo' : 'Clock Out Photo'}
              </h3>
              <button
                onClick={() => setPhotoModal({ open: false })}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex justify-center overflow-hidden">
              <img
                src={`data:image/jpeg;base64,${photoModal.photo}`}
                alt={photoModal.type === 'clockIn' ? 'Clock In Photo' : 'Clock Out Photo'}
                className="max-w-full max-h-[70vh] w-auto h-auto object-contain rounded"
                style={{ transform: 'rotate(90deg)' }}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Attendance;

